import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import type { LocationId } from '@brightlocal/shared';

import { locationQuery } from 'shared/modules/locations';
import { routes } from 'shared/routes';

interface Params {
  /** Router-context query client the route loader runs with. */
  queryClient: QueryClient;
  /** Location whose LSG report is checked. */
  locationId: LocationId;
}

/**
 * Route-level guard for a TanStack Router `loader`: sends a location with no LSG report to the
 * Local Search Grid page, which shows its no-report screen. A settings page never creates a report.
 * The location resolves through the `$id` loader's in-flight `locationQuery` request. A soft-failed
 * (`null`) location fails open, as `requireNonAdhocLocation` does.
 */
export const requireLsgReport = async ({ queryClient, locationId }: Params) => {
  const location = await queryClient.ensureQueryData(locationQuery(locationId));

  if (location == null) {
    return;
  }

  const hasLsgReport = location.reports?.lsg?.id != null;

  if (!hasLsgReport) {
    throw redirect(routes.localSearchGrid(locationId));
  }
};
